import Image from 'next/image';
import { AvailablePlan, AvailablePlans } from '../types/availablePlans';
import { ICON_CHECK, ICON_CLOSE } from './assets';

type CoverageRow = {
  description: string;
  rowType: 'boolean' | 'percentage_maximum' | 'maximum' | 'fixed' | 'text' | 'checkbox';
  values: Record<string, string | JSX.Element>;
};

export const extractUFfromBold = (html: string): string | null => {
  const regex = /<b>(UF\s*[\d.,]+|[\d.,]+\s*UF)<\/b>/i;
  const match = regex.exec(html);
  if (!match) return null;

  const raw = match[1].trim();
  const numberRegex = /[\d.,]+/;
  const numberMatch = numberRegex.exec(raw);

  return numberMatch ? `UF ${numberMatch[0]}` : null;
};

const findItem = (plan: AvailablePlan, categoryTitle: string, itemDescription: string) => {
  const cat = plan.coverage.health.details.categories.find((c) => c.title === categoryTitle);

  return cat?.items.find((i) => i.description === itemDescription);
};

export const IconCheck = () => <Image width={24} height={24} src={ICON_CHECK} alt="check" />;
export const IconClose = () => <Image width={24} height={24} src={ICON_CLOSE} alt="close" />;

export const generateCoverageRows = (plans: AvailablePlans): CoverageRow[] => {
  return [
    {
      description: 'Cubre preexistencias',
      rowType: 'boolean',
      values: Object.fromEntries(plans.map((p) => [p._id, IconCheck()])),
    },

    {
      description: 'Bonificación general',
      rowType: 'percentage_maximum',
      values: Object.fromEntries(plans.map((p) => [p._id, `${p.coverage.health.value}%`])),
    },
    {
      description: 'Tope anual reembolsable',
      rowType: 'text',
      values: Object.fromEntries(
        plans.map((p) => {
          const match = extractUFfromBold(p.cardCoverages[2]);

          return [p._id, match ?? '-'];
        })
      ),
    },

    {
      description: 'Seguro de vida',
      rowType: 'text',
      values: Object.fromEntries(
        plans.map((p) => {
          const match = extractUFfromBold(p.cardCoverages[3]);

          return [p._id, match ?? '-'];
        })
      ),
    },

    {
      description: 'Medicamentos genéricos',
      rowType: 'percentage_maximum',
      values: Object.fromEntries(
        plans.map((p) => {
          const item = findItem(p, 'Medicamentos con receta médica', 'Libre elección genéricos');

          return [p._id, item ? `${item.bonus.value.percentage}%` : '-'];
        })
      ),
    },
    {
      description: 'Med. no genéricos T. anual',
      rowType: 'percentage_maximum',
      values: Object.fromEntries(
        plans.map((p) => {
          const item = findItem(p, 'Medicamentos con receta médica', 'Libre elección no genéricos');
          const bonus = item?.bonus.value.percentage;
          const uf = item?.maximum?.value?.uf;

          return [p._id, item && bonus != null && uf != null ? `${bonus}% (Tope UF ${uf})` : '-'];
        })
      ),
    },

    {
      description: 'Psicología / Psiquiatría T. anual',
      rowType: 'maximum',
      values: Object.fromEntries(
        plans.map((p) => {
          const item = findItem(
            p,
            'Atención psiquiátrica y psicológica',
            'Hospitalización psiquiátrica y consulta psiquiátrica hospitalaria'
          );
          const uf = item?.maximum?.value?.uf;

          const terapiaOcupacional = findItem(
            p,
            'Atención psiquiátrica y psicológica',
            'Terapia Ocupacional'
          );

          const tieneTO = terapiaOcupacional != null;

          let label = '-';
          if (uf != null) {
            label = `Tope UF ${uf}`;
            if (tieneTO) {
              label += ' + T. ocupacional';
            }
          }

          return [p._id, label];
        })
      ),
    },

    {
      description: 'Óptica T. anual',
      rowType: 'percentage_maximum',
      values: Object.fromEntries(
        plans.map((p) => {
          const item = findItem(
            p,
            'Beneficios especiales',
            'Marcos, cristales ópticos y lentes de contacto'
          );
          const bonus = item?.bonus.value.percentage;
          const uf = item?.maximum?.value?.uf;

          return [p._id, item && bonus != null && uf != null ? `${bonus}% (Tope UF ${uf})` : '-'];
        })
      ),
    },
    {
      description: 'Cirugía Ocular',
      rowType: 'boolean',
      values: Object.fromEntries(
        plans.map((p) => {
          const item = findItem(p, 'Beneficios especiales', 'Cirugía Ocular Lasik');

          return [p._id, item ? IconCheck() : IconClose()];
        })
      ),
    },

    {
      description: 'Deducible GES/CAEC/AUGE',
      rowType: 'fixed',
      values: Object.fromEntries(
        plans.map((p) => {
          const item = findItem(p, 'Beneficios especiales', 'Deducible GES-CAEC-AUGE');
          const uf = item?.maximum?.value?.uf;

          return [p._id, item && uf != null ? `UF ${uf} anual` : IconClose()];
        })
      ),
    },

    {
      description: 'Asistencia mascota',
      rowType: 'boolean',
      values: Object.fromEntries(
        plans.map((p) => {
          const found = p.assistances?.some((a) =>
            a.description.includes('Asistencia médica para tu mascota')
          );

          return [p._id, found ? IconCheck() : IconClose()];
        })
      ),
    },
    {
      description: 'Deducible sin cargas',
      rowType: 'fixed',
      values: Object.fromEntries(
        plans.map((p) => {
          const desc = p.coverage.health.details.description;
          const regex = /sin cargas es de ([\d.,]+) UF/i;
          const match = regex.exec(desc);

          return [p._id, match ? `UF ${match[1]}` : '-'];
        })
      ),
    },
  ];
};

export const coveragesList = ['dental', 'catastrofico', 'deceso'];
